import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './login.css';
import apiClient from '../../axiosClient';
import { CircularProgress } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import { passwordInputPattern, textPattern } from '../../constants/patterns';
import { decodedJWT, getPdbID } from '../../utils/commonUtils';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';

const Login: React.FC = () => {
  // Early validation for required environment variables
  const currentEnv = process.env.REACT_APP_ENV ?? process.env.NODE_ENV;
  const isLocal = currentEnv === 'dev' || currentEnv === 'qa';
  
  if (!process.env.REACT_APP_SITE_KEY && !isLocal) {
    throw new Error('REACT_APP_SITE_KEY environment variable is required for non-local environments');
  }

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  const [captcha, setCaptchaToken] = useState<string|null>(null);
  const siteKey = process.env.REACT_APP_SITE_KEY || '';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      // API call to authenticate user
      const response = await apiClient.post('/login', {
        username,
        password,
        ...(isLocal ? {} : { captcha })
      });

      if (response.status === 200) {
        localStorage.setItem("token", response.data.token);
        const token: false | JWTTokenPayload = decodedJWT();
        const pdbID = getPdbID(token);
        // On successful login, navigate to the dashboard or another page
        if (pdbID) {
          navigate('/homepage');
        } else {
          navigate('/portfolio');
        }
      } else {
        setError(response.data.message);
      }
    } catch (error:any) {
      if (error.response) {
        setError(error.response.data.message || 'An error occurred!');
      } else {
        setError('An error occurred!');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Login</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleLogin}>
          <div className="input-box">
            <input
              title="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(textPattern, ""))}
              required
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
              maxLength={50}
            />
            <label>Username</label>
          </div>
          <div className="input-box">
            <input
              title="password"
              type="password"
              value={password}
              autoComplete="off"
              onChange={(e) => setPassword(e.target.value.replace(passwordInputPattern, ""))}
              required
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
              maxLength={30}
            />
            <label>Password</label>
          </div>
          <div style={{marginBottom: '15px'}}>
            <ReCAPTCHA sitekey={siteKey} onChange={(token) =>setCaptchaToken(token)}/>
          </div>
          <button type="submit" className="login-button" disabled={!captcha && !isLocal}>
            {loading ? (
              <CircularProgress color="inherit" />
            ) : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
